
/**
 * Utility functions for extracting, storing, and retrieving video thumbnails
 * with cross-browser compatibility support.
 */

import { toastService } from "@/utils/toastService";

/**
 * Attempts to extract a thumbnail from the first frame of a video file
 * Uses different approach based on browser capabilities
 */
export const extractVideoThumbnail = async (file: File): Promise<string | null> => {
  return new Promise((resolve) => {
    try {
      // Check if this is a video file
      if (!file.type.startsWith('video/')) {
        resolve(null);
        return;
      }

      // Detect Safari browser
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
                      (navigator.userAgent.includes('AppleWebKit') && !navigator.userAgent.includes('Chrome'));

      if (isSafari) {
        // For Safari, try the web playback API approach which has better compatibility
        extractSafariCompatibleThumbnail(file)
          .then(thumbnailUrl => {
            if (thumbnailUrl) {
              resolve(thumbnailUrl);
            } else {
              // If that fails too, use a placeholder
              resolve(getDefaultThumbnail(file.name));
            }
          })
          .catch(() => {
            resolve(getDefaultThumbnail(file.name));
          });
        return;
      }
      
      // Create elements for thumbnail extraction
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      
      // Set up video element with more robust settings
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true; // Important for iOS
      video.crossOrigin = 'anonymous';
      
      // Create object URL from the file
      const objectUrl = URL.createObjectURL(file);
      
      // Set up event listeners for video
      video.onloadedmetadata = () => {
        // For very short videos, use time 0
        // For longer videos, use a frame a bit into the video to avoid black frames
        const seekTime = video.duration < 1 ? 0 : Math.min(0.5, video.duration / 4);
        video.currentTime = seekTime;
      };
      
      // Handle errors
      video.onerror = (e) => {
        console.error('[thumbnailExtractor] Error loading video for thumbnail extraction:', e);
        URL.revokeObjectURL(objectUrl);
        resolve(getDefaultThumbnail(file.name));
      };
      
      // Handle seeking to specified time
      video.onseeked = () => {
        try {
          // Set canvas dimensions based on video
          // Default to modest resolution if video dimensions aren't available
          canvas.width = video.videoWidth || 320;
          canvas.height = video.videoHeight || 240;
          
          // Get canvas context and draw the current video frame
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            console.error('[thumbnailExtractor] Could not get 2D context from canvas');
            resolve(getDefaultThumbnail(file.name));
            return;
          }
          
          // Draw the current frame to the canvas
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          // Convert the canvas to a data URL with good quality but reasonable size
          const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.85);

          // Clean up
          URL.revokeObjectURL(objectUrl);
          
          resolve(thumbnailDataUrl);
        } catch (error) {
          console.error('[thumbnailExtractor] Error capturing video frame:', error);
          URL.revokeObjectURL(objectUrl);
          resolve(getDefaultThumbnail(file.name));
        }
      };
      
      // Start loading the video
      video.src = objectUrl;
      
      // Set a timeout in case the video takes too long to load
      setTimeout(() => {
        if (!video.videoWidth) {
          URL.revokeObjectURL(objectUrl);
          resolve(getDefaultThumbnail(file.name));
        }
      }, 3000);
      
    } catch (error) {
      console.error('[thumbnailExtractor] Error in thumbnail extraction:', error);
      resolve(getDefaultThumbnail(file.name || "video"));
    }
  });
};

/**
 * Alternative approach for Safari that tries to extract a thumbnail
 * using different browser APIs that might work in Safari
 */
const extractSafariCompatibleThumbnail = async (file: File): Promise<string | null> => {
  return new Promise((resolve, reject) => {
    try {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      canvas.width = 320;  // Use modest resolution by default
      canvas.height = 240; // Will adjust based on video aspect ratio
      
      const objectUrl = URL.createObjectURL(file);
      
      // The key differences for Safari:
      // 1. Try to use the play() method which sometimes works in Safari
      // 2. Use a timeout approach rather than relying on events
      // 3. Include CORS handling
      
      // Configure video element
      video.muted = true;
      video.playsInline = true;
      video.autoplay = false; // Important for Safari
      video.crossOrigin = 'anonymous';
      video.preload = 'auto';
      
      // Load the video
      video.src = objectUrl;
      
      // First wait for metadata to load
      video.onloadeddata = () => {
        // Adjust canvas size to match video aspect ratio
        if (video.videoWidth && video.videoHeight) {
          // Maintain aspect ratio but cap at reasonable size
          const maxDimension = 640;
          const aspectRatio = video.videoWidth / video.videoHeight;
          
          if (aspectRatio >= 1) {
            // Landscape video
            canvas.width = Math.min(video.videoWidth, maxDimension);
            canvas.height = canvas.width / aspectRatio;
          } else {
            // Portrait video
            canvas.height = Math.min(video.videoHeight, maxDimension);
            canvas.width = canvas.height * aspectRatio;
          }
        }
        
        // In Safari, we'll try to play and pause quickly
        try {
          video.currentTime = 0.1; // Seek to beginning
          
          // Try to grab frame after a short timeout
          setTimeout(() => {
            try {
              const ctx = canvas.getContext('2d');
              if (!ctx) {
                throw new Error('Could not get 2D context');
              }
              
              // Draw current frame to canvas
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              
              // Convert to data URL
              const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.85);
              
              // Validate the thumbnail isn't just a black frame
              if (isBlackFrame(ctx, canvas.width, canvas.height)) {
                // Try to play and take a frame after playback started
                playAndCapture(video, canvas, objectUrl, resolve, reject);
              } else {
                // Clean up
                URL.revokeObjectURL(objectUrl);
                resolve(thumbnailUrl);
              }
            } catch (e) {
              console.error('[thumbnailExtractor] Safari frame capture error:', e);
              // Try play method as last resort
              playAndCapture(video, canvas, objectUrl, resolve, reject);
            }
          }, 300);
        } catch (e) {
          console.error('[thumbnailExtractor] Safari currentTime error:', e);
          URL.revokeObjectURL(objectUrl);
          reject(e);
        }
      };
      
      video.onerror = (e) => {
        console.error('[thumbnailExtractor] Video load error in Safari:', e);
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Video load error'));
      };
      
      // Set a timeout in case nothing works
      setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Thumbnail extraction timeout'));
      }, 5000);
      
    } catch (error) {
      console.error('[thumbnailExtractor] Safari extraction error:', error);
      reject(error);
    }
  });
};

/**
 * Helper function to play and capture a video frame in Safari
 */
const playAndCapture = async (
  video: HTMLVideoElement, 
  canvas: HTMLCanvasElement, 
  objectUrl: string,
  resolve: (value: string | null) => void,
  reject: (reason?: unknown) => void
) => {
  try {
    // Try to use play() which can work in Safari in some cases
    const playPromise = video.play();
    
    if (playPromise !== undefined) {
      playPromise.then(() => {
        // Successfully playing, capture a frame
        setTimeout(() => {
          try {
            video.pause();
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('No context');
            
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.85);
            
            // Clean up
            URL.revokeObjectURL(objectUrl);
            resolve(thumbnailUrl);
          } catch (e) {
            console.error('[thumbnailExtractor] Frame capture after play error:', e);
            URL.revokeObjectURL(objectUrl);
            reject(e);
          }
        }, 100);
      }).catch((e) => {
        console.error('[thumbnailExtractor] Play failed in Safari:', e);
        URL.revokeObjectURL(objectUrl);
        reject(e);
      });
    } else {
      // Play not supported
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Video play not supported'));
    }
  } catch (e) {
    console.error('[thumbnailExtractor] Play and capture error:', e);
    URL.revokeObjectURL(objectUrl);
    reject(e);
  }
};

/**
 * Check if a canvas contains a black or nearly-black frame
 */
const isBlackFrame = (ctx: CanvasRenderingContext2D, width: number, height: number): boolean => {
  // Sample pixels from the canvas to see if it's just a black frame
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  // Check random pixels (performance optimization - checking every pixel would be slow)
  const sampleSize = 100;
  const pixelCount = width * height;
  let blackCount = 0;
  
  for (let i = 0; i < sampleSize; i++) {
    // Get a random pixel
    const randomPixel = Math.floor(Math.random() * pixelCount);
    const pixelStart = randomPixel * 4; // RGBA data
    
    // Check if this pixel is very dark (nearly black)
    const r = data[pixelStart];
    const g = data[pixelStart + 1];
    const b = data[pixelStart + 2];
    
    // Average brightness
    const brightness = (r + g + b) / 3;
    if (brightness < 20) { // Less than ~8% brightness
      blackCount++;
    }
  }
  
  // If more than 90% of sampled pixels are dark, consider it a black frame
  return blackCount > (sampleSize * 0.9);
};

/**
 * Get a default thumbnail URL if no custom thumbnail is available
 * This function picks a video-appropriate thumbnail that looks good
 */
export const getDefaultThumbnail = (videoIdentifier: string): string => {
  const placeholderOptions = [
    "/video-thumbnail-1.jpg",
    "/video-thumbnail-2.jpg",
    "/video-thumbnail-3.jpg"
  ];
  
  // Use a simple hash of the video name to consistently pick the same thumbnail
  const hash = videoIdentifier.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const placeholderIndex = Math.abs(hash) % placeholderOptions.length;
  
  // Fall back to a static path if needed
  return placeholderOptions[placeholderIndex] || "/video-placeholder.jpg";
};

// Local storage key for storing thumbnails
const THUMBNAIL_STORAGE_KEY = 'video-thumbnails';

/**
 * Stores a thumbnail in localStorage for later retrieval
 * @param videoUrl The URL of the video
 * @param thumbnailDataUrl The thumbnail as a data URL
 */
export const storeThumbnail = (videoUrl: string, thumbnailDataUrl: string): void => {
  try {
    // Create a shorter key by hashing the URL
    const videoKey = videoUrl.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0).toString(16);
    
    // Get existing thumbnails
    const thumbnailsJson = localStorage.getItem(THUMBNAIL_STORAGE_KEY) || '{}';
    const thumbnails = JSON.parse(thumbnailsJson);
    
    // Store the new thumbnail
    thumbnails[videoKey] = thumbnailDataUrl;
    
    // Save back to localStorage
    localStorage.setItem(THUMBNAIL_STORAGE_KEY, JSON.stringify(thumbnails));
  } catch (error) {
    console.error('[thumbnailExtractor] Failed to store thumbnail:', error);
  }
};

/**
 * Retrieves a thumbnail for a video from localStorage
 * @param videoUrl The URL of the video
 * @returns The thumbnail data URL or null if not found
 */
export const getThumbnail = (videoUrl: string): string | null => {
  try {
    // Create a shorter key by hashing the URL
    const videoKey = videoUrl.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0).toString(16);
    
    // Get thumbnails from localStorage
    const thumbnailsJson = localStorage.getItem(THUMBNAIL_STORAGE_KEY) || '{}';
    const thumbnails = JSON.parse(thumbnailsJson);
    
    // Return the thumbnail if it exists
    if (thumbnails[videoKey]) {
      return thumbnails[videoKey];
    }
    
    return null;
  } catch (error) {
    console.error('[thumbnailExtractor] Failed to retrieve thumbnail:', error);
    return null;
  }
};

/**
 * Convert a data URL to a File object that can be uploaded
 * @param dataUrl The data URL string
 * @param filename The desired filename for the resulting file
 * @returns A File object containing the data
 */
export const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  return new File([u8arr], filename, { type: mime });
};
